import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lightbulb, ExternalLink, BookOpen, ClipboardList } from "lucide-react";
import HighlightableText from "@/components/HighlightableText";
import SaveHighlightDialog from "@/components/SaveHighlightDialog";
import ListenButton from "@/components/ListenButton";
import { useAuth } from "@/context/AuthContext";

const TOPIC_ORDER = ["Recruitment", "Benefits", "Analytics", "Performance", "Training"];

function Body({ text }) {
  const lines = text.split("\n");
  const out = [];
  let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={out.length} className="list-disc pl-5 space-y-1.5">{list}</ul>); list = []; } };
  lines.forEach((ln, i) => {
    if (ln.startsWith("- ")) list.push(<li key={i} className="text-sm text-slate-700 leading-relaxed">{ln.slice(2)}</li>);
    else { flush(); if (ln.trim()) out.push(<p key={i} className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{ln}</p>); }
  });
  flush();
  return <div className="space-y-2">{out}</div>;
}

export default function BestPractices() {
  const { user } = useAuth();
  const [guides, setGuides] = useState([]);
  const [params, setParams] = useSearchParams();
  const [pending, setPending] = useState(null);
  const activeId = params.get("g") || guides[0]?.id;
  const active = guides.find((g) => g.id === activeId);

  useEffect(() => { api.get("/guides").then((r) => setGuides(r.data)); }, [user?.employer_context]);

  const byTopic = TOPIC_ORDER.map((t) => ({ topic: t, items: guides.filter((g) => g.topic === t) })).filter((t) => t.items.length);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Best Practices</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">HR Practice Guides</h1>
        <p className="text-sm text-slate-600 mt-1">Recruitment, benefits, KPIs, performance management, and training — every guide is sourced and citable by the assistant.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 space-y-5">
          {byTopic.map(({ topic, items }) => (
            <div key={topic}>
              <div className="text-[10px] uppercase tracking-widest text-[#C7761A] font-bold mb-2 px-1">{topic}</div>
              <div className="space-y-1.5">
                {items.map((g) => (
                  <button
                    key={g.id}
                    data-testid={`guide-${g.id}`}
                    onClick={() => setParams({ g: g.id })}
                    className={`w-full text-left p-3 rounded-xl border transition-colors ${
                      g.id === activeId ? "bg-[#0A2A5E] text-white border-[#0A2A5E]" : "bg-white border-slate-200 hover:border-[#F5A623] hover:bg-[#FFF7E8]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <Lightbulb className={`w-4 h-4 mt-0.5 flex-shrink-0 ${g.id === activeId ? "text-[#F5A623]" : "text-[#C7761A]"}`} />
                      <div>
                        <div className="text-sm font-semibold leading-snug">{g.title}</div>
                        <div className={`text-xs mt-0.5 line-clamp-2 ${g.id === activeId ? "text-slate-300" : "text-slate-500"}`}>{g.summary}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <section className="lg:col-span-8">
          {active && (
            <Card data-testid="guide-content" className="p-6 sm:p-8 bg-white border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-[#C7761A] font-bold">{active.topic}</div>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0A2A5E] mt-1">{active.title}</h2>
              <p className="text-sm text-slate-600 mt-2">{active.summary}</p>
              {active.framing && (
                <div data-testid="guide-framing" className={`mt-3 p-3 rounded-lg border text-xs ${active.employer_context === "unspecified" ? "border-slate-200 bg-[#F5F9FE] text-slate-600" : "border-[#F5A623] bg-[#FFF8EC] text-[#7C3F00]"}`}>
                  <span className="font-bold uppercase tracking-widest text-[10px] mr-2">{active.context_label}</span>{active.framing}
                  {active.employer_context === "unspecified" && <Link to="/app/chat" className="ml-1 underline">Set employer type</Link>}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span data-testid="guide-citation" className="text-xs px-2 py-1 bg-[#FDECD0] text-[#7C3F00] rounded-full font-semibold">Source: {active.citation}</span>
                {active.citation_note && <span data-testid="guide-citation-note" className="text-[11px] text-slate-600 italic w-full sm:w-auto">{active.citation_note}</span>}
                <a data-testid="guide-source-link" href={active.source_url} target="_blank" rel="noreferrer" className="text-xs text-[#0A2A5E] hover:underline inline-flex items-center gap-1 px-2 py-1">
                  Official reference <ExternalLink className="w-3 h-3" />
                </a>
                <Link to="/app/documents" className="text-xs text-[#0A2A5E] hover:underline inline-flex items-center gap-1 px-2 py-1"><BookOpen className="w-3 h-3" /> In Library as {active.doc_id}</Link>
                <Link to="/app/forms"><Button size="sm" variant="outline" className="h-7 text-xs border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]"><ClipboardList className="w-3 h-3 mr-1" /> Related forms</Button></Link>
                <ListenButton key={active.id} src={`/tts/guide/${active.id}`} label="Listen to this guide" costHint="~5–10¢" testid={`guide-listen-${active.id}`} />
              </div>

              <HighlightableText testid="guide-text" onSave={(t) => setPending({ text: t, source_type: "document", source_ref: active.doc_id, source_title: active.title, citation: active.citation })}>
                <div className="mt-6 space-y-7">
                  {active.sections.map((s, i) => (
                    <div key={i} className="print-break">
                      <h3 className="font-display font-bold text-[#0A2A5E] text-base mb-2 flex items-center gap-2">
                        <span className="w-1.5 h-5 rounded-full bg-[#F5A623] inline-block" /> {s.heading}
                      </h3>
                      <Body text={s.body} />
                    </div>
                  ))}
                </div>
              </HighlightableText>
              <p className="text-xs text-slate-400 mt-8 border-t border-slate-100 pt-3">Select any passage to save it to your Highlights with the source attached.</p>
            </Card>
          )}
        </section>
      </div>

      <SaveHighlightDialog pending={pending} onClose={() => setPending(null)} />
    </div>
  );
}
